/**
 * The material table — every selectable hue-anchor set, expressed in the roles `theme.ts` reads.
 *
 * Authorised by: design-direction §2 (Drystone owns surface; the accent may run bolder — §2
 * objection 2), §3 (base palette plus one focus accent, under the Intensity dial), §7 (accent
 * colour explicitly open), and `docs/DEVIATIONS.md` M11. The *vocabulary* — which materials
 * exist, and which tier each sits on — lives in `@carn/core/src/materials.ts` (RULINGS R29: one
 * declaration, three entry points); this file holds only the pixels.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────
 * THE ONE PROPERTY THAT MAKES A PAID MATERIAL LEGAL, AND HOW IT IS OBTAINED
 * ─────────────────────────────────────────────────────────────────────────────────────────
 *
 * Every stop in every ramp below was solved to the **exact WCAG relative luminance of the
 * corresponding Drystone stop**. Not "approximately as light"; the same luminance, to within the
 * 8-bit sRGB rounding. Hue was placed in OKLCH so the ramps are perceptually even rather than
 * sRGB-even, and chroma was gamut-mapped by *reduction* at constant hue (never by clipping
 * channels, which shifts hue).
 *
 * Two consequences follow, and they are the entire safety argument:
 *
 *   1. **Every fixed contrast pair is the same ratio in every material.** `text` on the page,
 *      `textSecondary` on a card, the focus ring on the page — none of these are solved by
 *      `theme.ts`; they are ramp stops read directly. Matching luminance stop-for-stop is what
 *      makes design-direction §3's "text contrast is fixed at accessible levels at every
 *      setting" survive a hue swap. Measured: light text-on-page is 14.20:1 in Drystone and
 *      14.06–14.26:1 across the four others. `test/materials.test.ts` asserts the bound.
 *   2. **Everything that carries an obligation is re-solved anyway.** Boundaries, marks, the
 *      accent, the compost gradient and the ground edge are computed by `atContrast`/
 *      `atLuminance` from the ratio they owe, against this material's own grounds. A material
 *      supplies an anchor hue; it never supplies a contrast.
 *
 * So a material is a change of hue and chroma, and structurally cannot be a change of legibility.
 * That is why gating one behind Plus does not gate a sensory setting — see M11, and
 * `@carn/core/src/materials.ts`, which carries the argument in full.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────
 * THE ROLES
 * ─────────────────────────────────────────────────────────────────────────────────────────
 *
 * Eight ramps per material, named for what the theme does with them rather than for their
 * colour — `oak` is a colour, `warm` is a job. `theme.ts` reads roles and never ramp names, so
 * a material is swapped by swapping this record and nothing else.
 *
 * A material may reuse one ramp for two roles: Drystone's `neutral` and `cool` are both `slate`,
 * which is exactly what makes `cooling` the state that sits level with the page.
 */

import { type MaterialId } from '@carn/core';

import { ember, gorse, lichen, moss, oak, slate, wool, type Ramp } from './palette';

export type MaterialRole =
  /** Structure: text, boundaries, the dark scheme's grounds, and the achromatic axis. */
  | 'neutral'
  /** The light scheme's page ground, the `released` state, and text drawn on the accent. */
  | 'ground'
  /** `temperature.warm` — the state the accent is deployed on. */
  | 'warm'
  /** `temperature.cooling` — sits level with the page. */
  | 'cool'
  /** `temperature.composting` — recedes past the page. */
  | 'quiet'
  /** `temperature.closed`. */
  | 'living'
  /** THE focus accent. One per material (design-direction §3). */
  | 'accent'
  /** The warmth of the deep night ground — Hearth's only survival (design-direction §2). */
  | 'glow';

export type MaterialRamps = Record<MaterialRole, Ramp>;

export type MaterialTokens = {
  readonly ramps: MaterialRamps;
  /**
   * Chroma ceiling applied to `glow` when it tints the dark scheme's deep ground.
   *
   * design-direction §2 authorises ember-on-dark warmth as a *hint*, and §3's "red is never a
   * state" forbids it becoming a hue. Every material's `glow` is a warm orange-red ramp for the
   * same reason Drystone's is — a glow in the dark is warm — so every material needs the same
   * ceiling, and `test/contrast.test.ts` scans the resolved token against `RED_CHROMA_FLOOR`.
   * A material with a cooler glow could afford a higher ceiling; none currently does.
   */
  readonly nightChroma: number;
};

/**
 * DRYSTONE — the reference, and the free default.
 *
 * These are the existing `palette.ts` ramps under their role names, and that is the point: the
 * materials feature is a generalisation of what already shipped, not a replacement for it.
 * `test/materials.test.ts` asserts that resolving any theme with `material: 'drystone'` produces
 * tokens byte-identical to the pre-materials build, which is M11's first standing condition
 * ("Drystone must never be degraded to make a paid material look better") made checkable.
 */
const drystone: MaterialTokens = {
  ramps: {
    neutral: slate,
    ground: wool,
    warm: oak,
    // Deliberately the same ramp as `neutral`: `cooling` is the state that sits level with the
    // page, and sharing the structural hue is how it reads as "at rest" rather than as a colour.
    cool: slate,
    quiet: lichen,
    living: moss,
    accent: gorse,
    glow: ember,
  },
  nightChroma: 0.5,
};

/**
 * CALCH — limestone. The Gower and the Great Orme: bleached bone, sea-light, high-key.
 *
 * The bright material. Its neutral is a warm stone grey rather than a blue-grey, so its dark
 * scheme is a warm stone dark rather than a night sky — the one material whose two schemes are
 * both warm. Accent: sea holly (*Eryngium maritimum*), the steel-blue thistle that grows on
 * Welsh dunes — a cool accent on a warm ground, which is the widest chromatic separation in the
 * set and the reason this material reads as *bright* rather than merely *pale*.
 */
const calch: MaterialTokens = {
  ramps: {
    neutral: [
      '#F9F7F5', '#F2EEE9', '#E7DFD5', '#D4CABA', '#BEAE96', '#A59174',
      '#8B7453', '#725A3C', '#59462C', '#42321E', '#2F2313', '#1F160C',
    ],
    ground: [
      '#FEFBF7', '#FDF7EE', '#F9F0E0', '#F2E4C9', '#E5D2AE', '#D2BA8B',
      '#B99D68', '#978054', '#766543', '#574A32', '#3C3221', '#261F13',
    ],
    warm: [
      '#F9F8F2', '#F2EFE3', '#E8E0C8', '#D6CBA6', '#C1AF76', '#AA9147',
      '#917405', '#755C00', '#5C4600', '#433300', '#312400', '#201600',
    ],
    cool: [
      '#F5F9FA', '#E9EFF3', '#D6E3EA', '#BBCEDA', '#97B5C6', '#7499AE',
      '#527C94', '#3B647B', '#2C4C60', '#1F3848', '#132834', '#0C1921',
    ],
    quiet: [
      '#F7F9F6', '#ECF0EA', '#DCE3D8', '#C5CFBF', '#A6B49D', '#87997C',
      '#687C5C', '#506545', '#3D4E34', '#2B3925', '#1E2919', '#121A0F',
    ],
    living: [
      '#F5F8F5', '#E9F0E9', '#D5E4D6', '#BAD1BC', '#94B999', '#709F77',
      '#4B8256', '#336A40', '#245331', '#173C22', '#0D2B16', '#081B0E',
    ],
    accent: [
      '#FBFBFF', '#F3F5FF', '#E6ECFF', '#D2DCFF', '#BAC8FF', '#A0B4FF',
      '#7A93FF', '#6177D2', '#4A5DA3', '#374579', '#252F51', '#161E34',
    ],
    glow: [
      '#FDF7F3', '#F9ECE1', '#F3D7C1', '#E8BA98', '#D79C6F', '#C37D49',
      '#A76635', '#88512B', '#6C4123', '#50301C', '#352115', '#22140D',
    ],
  },
  nightChroma: 0.5,
};

/**
 * GLO — anthracite. The south Wales valleys: blue-black coal, bone ash, copper gone verdigris.
 *
 * The weighty material, and the only one designed dark-first: its neutral carries the blue sheen
 * real anthracite has, so the night scheme is a true blue-black rather than a grey. Accent:
 * verdigris, for the Swansea copper works — "Copperopolis" — where the same metal that made the
 * valley's money turns green in its rain. A luminous cool accent is what a near-black ground
 * needs; a warm one would have read as the lamp, and the lamp is `warm`, one role over.
 */
const glo: MaterialTokens = {
  ramps: {
    neutral: [
      '#F7F8FB', '#ECEEF5', '#DAE1ED', '#C2CBDE', '#A3B0CC', '#8395B6',
      '#64779C', '#4C5F83', '#394966', '#28364C', '#1B2637', '#101824',
    ],
    ground: [
      '#FBFCFF', '#F5F8FF', '#EAF2FF', '#D9E6FD', '#C2D4F3', '#A7BEE5',
      '#88A2CD', '#6E85A8', '#576883', '#404C60', '#2A3442', '#1A212A',
    ],
    warm: [
      '#FCF8F3', '#F6EDE4', '#F0DDCA', '#E2C7A9', '#D3A97B', '#BE894F',
      '#A6691F', '#8A5200', '#6D3E00', '#512C00', '#3B1E00', '#281300',
    ],
    cool: [
      '#F6F9FB', '#EBF0F4', '#D8E1EC', '#BECDDC', '#9DB3C9', '#7C97B3',
      '#5C7999', '#456180', '#344B63', '#24374A', '#182736', '#0F1923',
    ],
    quiet: [
      '#F5F9F9', '#E9F0F1', '#D4E4E4', '#B8D0D1', '#92B7B9', '#6D9B9F',
      '#497F84', '#31666B', '#234F54', '#173B3E', '#0D292D', '#081B1D',
    ],
    living: [
      '#F5F9F5', '#E9F1EA', '#D4E5D7', '#B8D1BD', '#92B99B', '#6D9F7A',
      '#48845A', '#2F6B43', '#215332', '#143C23', '#0B2C18', '#071B0E',
    ],
    accent: [
      '#F0FFFD', '#D4FFFB', '#A2FBF7', '#55F2EE', '#00E2DE', '#00CBCA',
      '#00ACAB', '#008B8C', '#006C6D', '#005152', '#003738', '#012224',
    ],
    glow: [
      '#FEF7F5', '#FCEAE3', '#FAD4C6', '#F1B7A0', '#E39579', '#CF7657',
      '#B15F42', '#904C36', '#723C2B', '#552D22', '#391F18', '#24130F',
    ],
  },
  nightChroma: 0.5,
};

/**
 * GRUG — heather. The late-summer moor: bracken rust, bilberry, heather shadow.
 *
 * The vivid material, and it exists for a stated reason rather than for variety:
 * design-direction §3's governing principle is that "some users need vivid stimulation to
 * register anything; others are overwhelmed by a drop shadow", and the Intensity dial answers
 * the second half far better than the first — the colour channel can always take saturation
 * *away*, but it can only add as much as the anchors carry. Grug is the anchor set with more to
 * give. Accent: bell heather in flower, the one truly saturated thing on a Welsh moor in August.
 *
 * NOT KITE RED, AND NOT BRACKEN ORANGE EITHER. design-direction §2 offered "gorse yellow, kite
 * red" as the accent candidates, and §3 permits red in exactly one place — "no red anywhere
 * except optional user-chosen accent" — so a user-chosen kite-red accent is, on the plain words,
 * authorised. It is still not built, because `palette.ts` rejected kite red once already on a
 * reason a user's consent does not dissolve: "a user glancing at a red-accented warm loop cannot
 * know it isn't an alarm." The ambiguity is in the glance, not in the choice.
 *
 * A burnt-orange bracken accent was drawn next, at HSL hue 25° — outside the guard's 20°
 * tolerance, and apparently safe. `test/materials.test.ts` failed it, for the reason recorded
 * under ACCENT HUES below: an accent is not only ever rendered at full strength. At colour level
 * 1 the theme tints it 35% of the way toward the material's own neutral, and Grug's neutral is a
 * purple grey — high red, high blue, low green. Interpolating an orange toward that in sRGB holds
 * red high while dropping green and blue, so the tint path passes *through* red, landing on hue
 * 17° at chroma 0.169. A ramp-level check could never have caught it: the offending colour is in
 * neither ramp, only strictly between them.
 */
const grug: MaterialTokens = {
  ramps: {
    neutral: [
      '#F8F7FB', '#EEEEF6', '#E0E0EF', '#CAC9E0', '#AEADCF', '#9090BA',
      '#7373A2', '#5B5B87', '#45456A', '#323250', '#22243A', '#151626',
    ],
    ground: [
      '#FCFCFF', '#F8F7FF', '#F1F0FF', '#E3E3FF', '#D1D1F8', '#B9B8EB',
      '#9C9CD4', '#7F7FAE', '#636488', '#494A63', '#313244', '#1E1F2C',
    ],
    warm: [
      '#F9F7FD', '#F0EDFA', '#E4DCF7', '#D1C5ED', '#B8A7E2', '#9E88D1',
      '#8268BB', '#6A50A0', '#523D7F', '#3C2C60', '#2A1E47', '#1A132E',
    ],
    cool: [
      '#F7F8FB', '#EEEFF6', '#DFDFEF', '#C8C9E0', '#ACAED0', '#8E90BA',
      '#7073A2', '#595B87', '#44466A', '#313350', '#22233A', '#151626',
    ],
    quiet: [
      '#F7F8FB', '#ECEEF5', '#DCE0ED', '#C4CBDE', '#A5B0CC', '#8693B6',
      '#67779D', '#505E83', '#3C4966', '#2B354C', '#1D2538', '#121824',
    ],
    living: [
      '#F5F9F4', '#EAF0E8', '#D8E4D4', '#BDD1B8', '#9AB994', '#789D71',
      '#56814F', '#3D6838', '#2D522A', '#1E3B1D', '#132B12', '#0C1B0B',
    ],
    accent: [
      '#FFFAFE', '#FFF2FC', '#FFE5F8', '#FFCEF4', '#FFB1F1', '#FF8EF0',
      '#EB65DE', '#BE52B5', '#94408E', '#6E316A', '#492147', '#2F152E',
    ],
    glow: [
      '#FEF8F5', '#FBEAE4', '#F7D5C7', '#EDB8A2', '#DE977C', '#C9795B',
      '#AD6246', '#8C4F38', '#6F3E2D', '#522F23', '#382019', '#22140F',
    ],
  },
  nightChroma: 0.5,
};

/**
 * LLANW — the tide. The Dyfi and Mawddach estuaries: silt, dove-grey, saltmarsh, eelgrass.
 *
 * The cool material, and Calch's deliberate opposite: the two share a luminance profile exactly
 * (as all materials do) and sit at opposite ends of the temperature axis, so a user who finds
 * Calch's warmth tiring has somewhere to go that is equally light. Accent: kingfisher — *glas y
 * dorlan*, "the blue of the riverbank" — the one flash of pure colour on an estuary in winter.
 *
 * FREE, since RULINGS R182: this paragraph is the reason. A user who needed exactly this escape
 * could not reach it without paying while Drystone was the only free material — see
 * `@carn/core`'s `MATERIAL_TIER`, and `./temperature.ts`, which measures the claim this
 * paragraph makes instead of trusting it (the measurement does not agree with the prose in the
 * dark scheme; that file says so plainly rather than rounding it off).
 */
const llanw: MaterialTokens = {
  ramps: {
    neutral: [
      '#F5F9F9', '#E9F0F2', '#D4E4E7', '#B9CFD5', '#93B6BE', '#6F9AA6',
      '#4C7F8C', '#346673', '#264F5A', '#193A43', '#0F2830', '#09191F',
    ],
    ground: [
      '#F6FEFF', '#EDFBFE', '#DDF6FA', '#C4EDF4', '#A5DDE7', '#7EC8D6',
      '#55ADBE', '#448E9C', '#376F7A', '#2B525A', '#1B373E', '#102327',
    ],
    warm: [
      '#FAF8F2', '#F4EEE4', '#ECE0CA', '#DCC9A8', '#C9AC7A', '#B38E4D',
      '#9A7019', '#7E5700', '#624300', '#4A3100', '#352200', '#231400',
    ],
    cool: [
      '#F6F8FB', '#EAF0F4', '#D7E2EC', '#BCCEDB', '#99B3C8', '#7697B1',
      '#557B98', '#3E627E', '#2E4C63', '#20374A', '#152735', '#0C1922',
    ],
    quiet: [
      '#F5F8F7', '#EAF0EE', '#D5E3DE', '#BBD0C9', '#95B7AD', '#729C90',
      '#4F8073', '#36675B', '#285147', '#1A3B33', '#0F2A24', '#091B17',
    ],
    living: [
      '#F5F9F6', '#E8F1EB', '#D3E5D9', '#B6D3C0', '#8DBA9E', '#679F7F',
      '#40845F', '#256B48', '#195337', '#0E3E28', '#052B1B', '#041B10',
    ],
    accent: [
      '#F8FDFF', '#ECF8FF', '#D3F0FF', '#ACE5FF', '#77D6FF', '#00C5FD',
      '#00A6D5', '#0087AD', '#006A87', '#004F65', '#063544', '#05222B',
    ],
    glow: [
      '#FDF8F5', '#F9ECE4', '#F4D6C7', '#E8B9A2', '#D79B7C', '#C27D5A',
      '#A76545', '#875137', '#6B402D', '#4F3122', '#362119', '#21140F',
    ],
  },
  nightChroma: 0.5,
};

/**
 * FFION — the foxglove, *Digitalis purpurea*. Welsh hedgerows, woodland edges and cleared
 * ground in June: blush, mauve shade, deep pink spires, and the hard green of the leaf.
 *
 * THE GAP IT FILLS. The other five run cool, earthy or rugged — stone, coal, tide, moor, wall.
 * Nothing in the set was pink or violet, and "the landscape has no pink in it" is simply false
 * about Wales in June. Ffion is the warm-chromatic corner of the space, and it is named for the
 * plant rather than for anybody it is imagined to suit: *ffion* is the Welsh word for foxglove
 * and a Welsh given name meaning the same thing, so the material is named the way every other
 * one is — after the thing it is the colour of.
 *
 * WHY ITS ACCENT IS VIOLET AND NOT PINK. The rule recorded under ACCENT HUES below binds hardest
 * here: Ffion's neutral is a mauve — high red, high blue, low green — and `tint` mixes the accent
 * toward it in sRGB at every colour level below 4. A warm accent would travel straight through
 * red, which is the defect that killed Grug's first accent. A violet accent sits on the same side
 * of the wheel as the neutral, so the whole tint path stays in the violet-magenta arc and never
 * approaches hue 0. The pink lives in the grounds and the warm card, where it belongs, and the
 * accent is the deeper note underneath it.
 */
const ffion: MaterialTokens = {
  ramps: {
    neutral: [
      '#FAF7F8', '#F4EDF0', '#ECDDE5', '#DDC5D2', '#C9A7BA', '#B188A0',
      '#976986', '#7C516D', '#613F56', '#482D3F', '#341E2D', '#22131D',
    ],
    ground: [
      '#FFFBFD', '#FFF5F9', '#FFECF3', '#FFDCEA', '#F6C8D9', '#E8ADC3',
      '#D08FA8', '#AA7389', '#855B6B', '#62434E', '#432D35', '#2B1C21',
    ],
    warm: [
      '#FDF7FA', '#F9EBF2', '#F5DAE7', '#E9C1D5', '#DA9FBE', '#C67EA5',
      '#AD5C8A', '#914472', '#723259', '#562442', '#3F1730', '#290F1F',
    ],
    cool: [
      '#F9F7FB', '#F1EEF4', '#E4DEED', '#D1C7DD', '#B8AACB', '#9D8CB4',
      '#816F9B', '#685781', '#514365', '#3C304C', '#2A2237', '#1B1523',
    ],
    quiet: [
      '#FAF7FA', '#F2EEF3', '#E7DDE8', '#D4C7D7', '#BDA9C2', '#A38CAA',
      '#886E90', '#705677', '#56425C', '#3F2F45', '#2D2132', '#1D1520',
    ],
    living: [
      '#F5F9F5', '#E8F1EA', '#D4E5D7', '#B8D2BE', '#91B99D', '#6C9F7C',
      '#47835D', '#2E6A46', '#205235', '#133C25', '#0A2C19', '#061C0F',
    ],
    accent: [
      '#FCFCFF', '#F7F4FF', '#EEE9FF', '#E1D8FF', '#D0C2FF', '#BFAAFF',
      '#A687FE', '#856DCF', '#6755A1', '#4B4077', '#322C50', '#1F1C34',
    ],
    glow: [
      '#FEF8F5', '#FBEBE5', '#F6D5CA', '#ECB7A7', '#DD9782', '#C87962',
      '#AB624D', '#8B4F3E', '#6E3E31', '#522F26', '#37201B', '#221310',
    ],
  },
  nightChroma: 0.5,
};

/**
 * PRIDE — the six-stripe flag, drawn by the temperature ladder rather than painted across it.
 *
 * AUTHORISED BY `docs/RULINGS.md` R196 (founder decision), recorded as a departure at
 * `docs/DEVIATIONS.md` `FD8`. It is the only material in this table that needs a ruling to exist,
 * for the reason set out under THE RED EXEMPTION below. Read R196 before changing anything here.
 *
 * WHAT MAKES IT THE FLAG AND NOT SIX ARBITRARY HUES. design-direction §4 fixes the vertical axis
 * as the temperature metaphor made spatial — warm at the top, cooling below, compost literally
 * downward — so a user scrolling the home surface travels DOWN a sequence that already exists.
 * The stripes are laid along that sequence in their own order, one per role:
 *
 *     warm      red      #E40303   the top of the ladder, the loop with the user's attention
 *     cool      orange   #FF8C00   cooling, the band below it
 *     ground    yellow   #FFED00   the page itself, and `released` — pale, at wool's luminances
 *     living    green    #008026   `closed`
 *     quiet     violet   #750787   `composting`, receding past the page — the bottom of the axis
 *     accent    blue     #004DFF   the one high-visibility mark, on the current warm focus
 *
 * The violet is carried at the flag's hue *direction* rather than at its literal angle: solved at
 * Lichen's luminances it lands at HSL 271° instead of 292°. That is not drift, it is the
 * separation gate doing its job — Ffion's `quiet` is a mauve at 286° and the light scheme's deep
 * ground is mixed from `quiet[6]`, so the two page bottoms measured ΔE00 **1.98** against a floor
 * of 3. Moving 20° toward blue and tripling the chroma is what makes Carn's compost gradient
 * distinguishable from Ffion's; the stripe is still unmistakably the violet one.
 *
 * WHY BLUE IS THE ACCENT AND VIOLET IS NOT, WHICH IS NOT AN AESTHETIC PREFERENCE. Two measured
 * reasons, in the order they bind:
 *
 *   1. **The violet corner of accent space is full.** Resolved at the shipped default dial, a
 *      violet accent lands ΔE00 **1.63** from Grug's bell heather in light and **1.83** in dark —
 *      below the just-noticeable difference, and far under the ΔE 4 floor
 *      `test/materials.test.ts` holds every pair of materials to. Two materials whose single
 *      strongest identity signal is the same colour are not two materials. Blue clears it, and
 *      the flag has one.
 *   2. **Green would have cleared the floor and been the wrong answer anyway.** The accent is
 *      painted on the current warm focus, and in this material that card is red — so a green mark
 *      on a red card is the one pairing common red-green colour blindness cannot separate.
 *      §3's redundancy rule means no *state* would be lost (position, icon and label still carry
 *      it), so this is a quality choice rather than a correctness one, and it is a poor one to
 *      make in a material about being seen. Blue against red survives every common CVD type.
 *
 * The stripes still all land: violet takes `composting`, the bottom of the vertical axis, which
 * is where the flag's last stripe sits anyway.
 *
 * Every hue is therefore doing semantic work — it is a role's anchor, not decoration — which is
 * what keeps §3's redundancy rule satisfied: position, icon and label carry the state exactly as
 * they do in every other material, and the hue carries identity. §2 objection 3's ban on ornament
 * is about motifs that pretend to be meaning; this is the existing meaning, coloured.
 *
 * THE HUE-PER-ROLE SHAPE IS NOT NEW. Grug's `warm` is a violet and its `cool` a purple-grey;
 * Ffion's `warm` is pink and its `cool` a violet. A role is a job, not a temperature — `theme.ts`
 * reads roles and never ramp names — so nothing here departs from how the table already works.
 *
 * THE RED EXEMPTION, WHICH IS THE ONLY REAL DEPARTURE. design-direction §3's "red is never a
 * state" is enforced by `test/materials.test.ts`, which scans every resolved token of every
 * material and rejects anything within 20° of hue 0 at chroma ≥ 0.12. `warm` here is the flag's
 * red and fails that scan by construction. R196 exempts THIS MATERIAL AND ONLY THIS MATERIAL, and
 * the exemption is a named list in `./colour.ts` (`RED_GATE_EXEMPT`) rather than a loosened
 * threshold, so the gate still holds at full strength over the other six.
 *
 * The objection the exemption overrides is on the record and was not answered, only outweighed:
 * `palette.ts` rejected kite red as the default accent because "a user glancing at a red-accented
 * warm loop cannot know it isn't an alarm", and that ambiguity is in the glance rather than in the
 * choice, so consent does not dissolve it. Three things narrow the cost, none of which dissolves
 * it either:
 *
 *   1. **Nothing in Carn is ever an alarm**, so there is no alarm rendering for a red card to be
 *      confused WITH. There is no overdue state, no danger ramp, no red badge anywhere in the
 *      product, and none of those can be added — the same gate that this material is exempt from
 *      is what stops them.
 *   2. **It is never the default and never arrives unasked.** `DEFAULT_MATERIAL` is Drystone; a
 *      user reaches this material by choosing it by name on a settings page.
 *   3. **At colour level 0 it is not red at all.** Every temperature surface collapses onto the
 *      material's own achromatic anchor, which here is a green-grey. The low-stim reference
 *      rendering — the one design-direction §3 objection 2 protects hardest — never sees the hue.
 *
 * WHY THE NEUTRAL IS GREEN-GREY. The six stripes fill six roles and leave `neutral` (the
 * structural axis: text, boundaries, the dark scheme's grounds) and `glow` (the night ground's
 * warmth, an orange-red hint in every material) to be chosen. `neutral` must be near-achromatic
 * and must not collide with any other material's anchor, and the anchors already in the table are
 * blue-grey (Drystone), warm stone (Calch), blue-black (Glo), purple-grey (Grug), teal-grey
 * (Llanw) and mauve (Ffion). Green-grey is the unclaimed corner, and it puts the flag's green at
 * the centre of the material's own monochrome — which is what colour level 0 renders.
 *
 * NAMED FOR THE THING IT IS THE COLOUR OF, like every other material in this table. `Pride` is
 * the flag; the flag is what the ramps are. It is not named for anybody it is imagined to suit.
 */
const pride: MaterialTokens = {
  ramps: {
    neutral: [
      '#F7F8F7', '#EDEFED', '#DDE2DD', '#C6CDC6', '#A9B2A9', '#8B978B',
      '#6F7A6E', '#576257', '#424C42', '#2F382F', '#202720', '#131913',
    ],
    ground: [
      '#FDFCF8', '#F9F9F1', '#F3F2E4', '#E8E7D0', '#D7D5B5', '#C1BF98',
      '#A6A37B', '#88855E', '#6B6844', '#504D2C', '#373419', '#23210D',
    ],
    warm: [
      '#FFF4F3', '#FFE7E3', '#FDCFC8', '#F4AEA2', '#E68678', '#D26052',
      '#BA4437', '#9F2A20', '#86150E', '#6B0000', '#4D0000', '#370000',
    ],
    cool: [
      '#FFF6F0', '#FEECDE', '#FDDAC1', '#F7C098', '#EA9F64', '#D77E2D',
      '#B56100', '#914D00', '#713B00', '#552A00', '#3D1D00', '#291100',
    ],
    quiet: [
      '#F9F5FF', '#F2E8FF', '#E7D5FF', '#D8B8FF', '#C794FE', '#B171F1',
      '#9953D9', '#8037BD', '#691FA1', '#530484', '#3B0062', '#290046',
    ],
    living: [
      '#F0F9F0', '#E0F0E0', '#C5E3C6', '#9ECFA0', '#71B777', '#469F50',
      '#208532', '#006B1E', '#005516', '#00400F', '#002E08', '#001F04',
    ],
    accent: [
      '#FAFCFF', '#F1F6FF', '#E2ECFF', '#CCDEFF', '#B0CBFF', '#92B7FF',
      '#6698FF', '#3574FF', '#1854E0', '#0339BA', '#002487', '#00155D',
    ],
    glow: [
      '#FFF7F4', '#FDEAE3', '#F8D5C7', '#EFB7A1', '#E29678', '#D07651',
      '#B75C34', '#9D441A', '#833002', '#652300', '#471600', '#300C00',
    ],
  },
  nightChroma: 0.5,
};

/**
 * ACCENT HUES — READ THIS BEFORE CHANGING ONE.
 *
 * The six accents are gorse yellow (HSL 43°), verdigris (178°), kingfisher (196°), sea holly
 * (227°), bell heather (326°) and — since R196 — the Pride flag's violet (292°). Two constraints
 * shaped that spread, and only the first is obvious:
 *
 *  1. **They must be far apart**, because the accent is the strongest identity signal a material
 *     has — design-direction §3 deploys it on exactly one thing, the user's current warm focus,
 *     so it is seen rarely and must be unmistakable when it is. The three cool accents sit on
 *     radically different grounds (near-black anthracite, dove-grey silt, bleached bone), which
 *     is what lets them be 20–30° apart without reading as the same colour.
 *
 *  2. **The tint path from the accent to the material's own neutral must not cross red**, which
 *     is the constraint that is invisible until a gate finds it. `theme.ts`'s `tint` mixes the
 *     accent toward the neutral in sRGB by `CHROMA_BY_LEVEL` — 35% at colour level 1 — and an
 *     sRGB interpolation between two hues does not travel the short way round a hue wheel; it
 *     travels straight through the middle of the RGB cube. A warm accent mixed toward a
 *     purple-grey neutral (high red, high blue, low green) keeps red high while green and blue
 *     fall, and arrives at red. Grug's first accent did exactly this and failed
 *     `test/materials.test.ts` at hue 17°.
 *
 *     The practical rule: if a material's neutral leans purple or blue, its accent must not sit
 *     in HSL 0–40°. Check the *tinted* colour at every level, not the ramp.
 */

/**
 * THE TABLE, keyed by `MaterialId` from `@carn/core` so it is a REAL compile-time exhaustiveness
 * check: adding an id there without adding ramps here is a type error, which is the failure mode
 * worth making impossible — a material that exists in the picker and has no pixels.
 *
 * It used to be keyed by a hand-written literal union that merely happened to list the same five
 * names, so the comment claimed a guarantee the declaration did not provide and a sixth material
 * would have compiled clean with no pixels at all. An adversarial review caught it. The import is
 * free: `theme.ts` already depends on `@carn/core` for `Temperature`.
 */
export const MATERIAL_TOKENS: Record<MaterialId, MaterialTokens> = {
  drystone,
  calch,
  glo,
  grug,
  llanw,
  ffion,
  pride,
};

